<template>
    <teleport to="body">
        <transition name="modal" appear>
            <div v-if="modelValue" class="modal-overlay" @click="handleOverlayClick">
                <div class="modal-container" :style="{ maxWidth: modalWidth }" @click.stop>
                    <div class="modal-header" v-if="title || $slots.header">
                        <slot name="header">
                            <h3 class="modal-title">{{ title }}</h3>
                        </slot>
                        <button class="modal-close" @click="handleClose" :disabled="loading">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>

                    <div class="modal-body">
                        <slot />
                    </div>

                    <div class="modal-footer" v-if="$slots.footer">
                        <slot name="footer" />
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
export default {
    name: 'BaseModal',
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        },
        closeOnOverlay: {
            type: Boolean,
            default: false
        },
        width: {
            type: String,
            default: '500px'
        },
        size: {
            type: String,
            default: 'md',
            validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl'].includes(value)
        }
    },
    emits: ['update:modelValue', 'close'],
    computed: {
        modalWidth() {
            // If width prop is explicitly set, use it (for backward compatibility)
            if (this.width !== '500px') {
                return this.width
            }

            // Otherwise, use size-based widths
            const sizeMap = {
                'sm': '400px',
                'md': '500px',
                'lg': '800px',
                'xl': '1200px',
                '2xl': '1400px'
            }

            return sizeMap[this.size] || sizeMap.md
        }
    },
    mounted() {
        if (this.modelValue) {
            document.body.style.overflow = 'hidden'
        }
    },
    unmounted() {
        document.body.style.overflow = ''
    },
    watch: {
        modelValue(newValue) {
            if (newValue) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = ''
            }
        }
    },
    methods: {
        handleClose() {
            if (!this.loading) {
                this.$emit('update:modelValue', false)
                this.$emit('close')
            }
        },
        handleOverlayClick() {
            if (this.closeOnOverlay && !this.loading) {
                this.handleClose()
            }
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    padding: 24px 24px 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.modal-title {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0;
}

.modal-close {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease-in-out;
}

.modal-close:hover {
    background-color: #f3f4f6;
    color: #374151;
}

.modal-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-body {
    padding: 24px;
    flex: 1;
    overflow-y: auto;
}

.modal-footer {
    padding: 0 24px 24px 24px;
    flex-shrink: 0;
}

/* Transition animations */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.25s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.95);
}

/* Responsive */
@media (max-width: 640px) {
    .modal-container {
        margin: 0;
        min-height: 100vh;
        border-radius: 0;
    }

    .modal-overlay {
        padding: 0;
    }
}
</style>
